import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { t } from '../i18n';

const API = window.location.origin + '/api';

interface OfferItem { product_id: string; quantity: string | number; }

const NewOffer = () => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const navigate = useNavigate();
  const [products, setProducts] = useState<any[]>([]);
  const [haveItems, setHaveItems] = useState<OfferItem[]>([{ product_id: '', quantity: '1' }]);
  const [wantItems, setWantItems] = useState<OfferItem[]>([{ product_id: '', quantity: '1' }]);
  const [cashAdjustment, setCashAdjustment] = useState(0);
  const [cashDirection, setCashDirection] = useState('request');
  const [note, setNote] = useState('');
  const [expiresDays, setExpiresDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [filterHave, setFilterHave] = useState('Au');
  const [filterWant, setFilterWant] = useState('Au');

  useEffect(() => { axios.get(`${API}/products`, { headers }).then(res => setProducts(res.data.products || [])); }, []);

  const addItem = (side: 'have' | 'want') => {
    if (side === 'have') setHaveItems([...haveItems, { product_id: '', quantity: 1 }]);
    else setWantItems([...wantItems, { product_id: '', quantity: 1 }]);
  };

  const removeItem = (side: 'have' | 'want', index: number) => {
    if (side === 'have') setHaveItems(haveItems.filter((_, i) => i !== index));
    else setWantItems(wantItems.filter((_, i) => i !== index));
  };

  const updateItem = (side: 'have' | 'want', index: number, field: string, value: any) => {
    if (side === 'have') {
      const updated = [...haveItems];
      updated[index] = { ...updated[index], [field]: value };
      setHaveItems(updated);
    } else {
      const updated = [...wantItems];
      updated[index] = { ...updated[index], [field]: value };
      setWantItems(updated);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const expires_at = new Date(Date.now() + expiresDays * 24 * 60 * 60 * 1000).toISOString();
      const cash_adjustment = cashDirection === 'request' ? cashAdjustment : -cashAdjustment;
      await axios.post(`${API}/offers`, {
        have_items: haveItems.filter(i => i.product_id).map(i => ({ product_id: parseInt(i.product_id), quantity: parseInt(String(i.quantity)) || 1 })),
        want_items: wantItems.filter(i => i.product_id).map(i => ({ product_id: parseInt(i.product_id), quantity: parseInt(String(i.quantity)) || 1 })),
        cash_adjustment, note, expires_at
      }, { headers });
      navigate('/orderbook');
    } catch (err) {}
    setLoading(false);
  };

  const metals = ['Au', 'Ag', 'Pt', 'Pd'];

  const ItemSelector = ({ side, items, filter, setFilter }: { side: 'have'|'want', items: OfferItem[], filter: string, setFilter: (m: string) => void }) => (
    <div>
      <div className="flex gap-1 mb-3">
        {metals.map(m => (
          <button key={m} type="button" onClick={() => setFilter(m)}
            className={`px-3 py-1 rounded-lg text-xs font-medium border ${filter === m ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}>
            {m}
          </button>
        ))}
      </div>
      {items.map((item, index) => (
        <div key={index} className="flex gap-2 mb-2 items-center">
          <select value={item.product_id} onChange={e => updateItem(side, index, 'product_id', e.target.value)}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
            <option value="">Select product</option>
            {products.filter(p => p.metal === filter).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <input
            key={`${side}-${index}-qty`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            defaultValue={String(item.quantity)}
            onBlur={e => updateItem(side, index, 'quantity', e.target.value)}
            className="w-16 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none text-center" />
          {items.length > 1 && (
            <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); removeItem(side, index); }} className="text-red-400 hover:text-red-600 text-lg leading-none flex-shrink-0">×</button>
          )}
        </div>
      ))}
      <button type="button" onClick={() => addItem(side)}
        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 mt-1">
        <span className="text-lg leading-none">+</span> Add item
      </button>
    </div>
  );

  const validHave = haveItems.filter(i => i.product_id).length > 0;
  const validWant = wantItems.filter(i => i.product_id).length > 0;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('newOffer', 'New Offer')}</h1>
      <p className="text-gray-400 text-sm mb-6">Post a new swap offer to the order book.</p>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm max-w-2xl">

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-3 block">I Have</label>
            <ItemSelector side="have" items={haveItems} filter={filterHave} setFilter={setFilterHave} />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-3 block">I Want</label>
            <ItemSelector side="want" items={wantItems} filter={filterWant} setFilter={setFilterWant} />
          </div>
        </div>

        <div className="mb-6">
          <label className="text-xs text-gray-500 mb-2 block">{t('cashAdjustment', 'Cash Adjustment')} (EUR)</label>
          <div className="flex gap-2 mb-2">
            <button onClick={() => setCashDirection('request')}
              className={`flex-1 py-2 rounded-lg text-xs font-medium border ${cashDirection === 'request' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}>
              {t('iRequest', 'I Request')}
            </button>
            <button onClick={() => setCashDirection('offer')}
              className={`flex-1 py-2 rounded-lg text-xs font-medium border ${cashDirection === 'offer' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}>
              {t('iOffer', 'I Offer')}
            </button>
          </div>
          <input type="text" inputMode="decimal" pattern="[0-9]*"
            defaultValue="0"
            onBlur={e => setCashAdjustment(parseFloat(e.target.value) || 0)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" placeholder="0" />
        </div>

        <div className="mb-6">
          <label className="text-xs text-gray-500 mb-1 block">{t('note', 'Note')} (optional)</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none"
            placeholder="Any additional notes..." />
        </div>

        <div className="mb-6">
          <label className="text-xs text-gray-500 mb-1 block">{t('expires', 'Expires in')}</label>
          <select value={expiresDays} onChange={e => setExpiresDays(parseInt(e.target.value))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
            <option value={3}>3 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>

        <button onClick={handleSubmit} disabled={loading || !validHave || !validWant}
          className="w-full py-3 rounded-lg text-white font-semibold text-sm disabled:opacity-50"
          style={{ backgroundColor: '#1a1a2e' }}>
          {loading ? 'Posting...' : 'Post Offer'}
        </button>
      </div>
    </div>
  );
};

export default NewOffer;
